// src/pages/PendingApplications.js
import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

export default function PendingApplications() {
  const [applications, setApplications] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState("");
  const [currentAdmin, setCurrentAdmin] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        setApplications([]);
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        const admin = userSnap.exists() && userSnap.data().role === "admin";

        setIsAdmin(admin);
        setCurrentAdmin({
          uid: currentUser.uid,
          email: currentUser.email || "",
        });

        if (!admin) {
          setApplications([]);
          setLoading(false);
          return;
        }

        const appsQuery = query(
          collection(db, "applications"),
          where("status", "==", "pending"),
          orderBy("submittedAt", "asc")
        );

        const appsSnap = await getDocs(appsQuery);
        const appsData = appsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setApplications(appsData);
      } catch (error) {
        console.error("Error fetching pending applications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const approveApplication = async (id, app) => {
    if (!currentAdmin?.uid) {
      alert("You must be logged in as an admin.");
      return;
    }

    const confirmed = window.confirm(
      `Approve application for ${app?.name || app?.email || "this applicant"}?\n\nThis will trigger account creation and the temporary password approval email.`
    );

    if (!confirmed) return;

    setProcessingId(id);

    try {
      await updateDoc(doc(db, "applications", id), {
        status: "approved",
        reviewedAt: serverTimestamp(),
        reviewedByUid: currentAdmin.uid,
        reviewedByEmail: currentAdmin.email || "",
      });

      setApplications((prev) => prev.filter((application) => application.id !== id));
    } catch (error) {
      console.error("Error approving application:", error);
      alert("Failed to approve application.");
    } finally {
      setProcessingId("");
    }
  };

  const rejectApplication = async (id, app) => {
    if (!currentAdmin?.uid) {
      alert("You must be logged in as an admin.");
      return;
    }

    const confirmed = window.confirm(
      `Reject application for ${app?.name || app?.email || "this applicant"}?`
    );

    if (!confirmed) return;

    setProcessingId(id);

    try {
      await updateDoc(doc(db, "applications", id), {
        status: "rejected",
        reviewedAt: serverTimestamp(),
        reviewedByUid: currentAdmin.uid,
        reviewedByEmail: currentAdmin.email || "",
      });

      setApplications((prev) => prev.filter((application) => application.id !== id));
    } catch (error) {
      console.error("Error rejecting application:", error);
      alert("Failed to reject application.");
    } finally {
      setProcessingId("");
    }
  };

  return (
    <Layout>
      <div className="flex flex-col gap-3 mb-6">
        <div>
          <h2 className="text-3xl font-bold text-[#B87333]">Pending Applications</h2>
          <p className="text-zinc-400 mt-1">
            Review and process new membership applications.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-zinc-300">Loading...</p>
      ) : !isAdmin ? (
        <p className="text-zinc-400">You do not have access to this page.</p>
      ) : applications.length === 0 ? (
        <p className="text-zinc-400">No pending applications.</p>
      ) : (
        <div className="space-y-4">
          {applications.map((app) => {
            const isProcessing = processingId === app.id;

            return (
              <div
                key={app.id}
                className="bg-zinc-900 p-4 rounded-lg border border-zinc-800"
              >
                <div className="flex flex-col gap-1">
                  <p className="text-zinc-100 font-semibold">{app.name}</p>
                  <p className="text-zinc-400 break-all">{app.email}</p>
                  <p className="text-zinc-400">DOB: {app.dob}</p>
                  {app.phone ? <p className="text-zinc-400">Phone: {app.phone}</p> : null}
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => approveApplication(app.id, app)}
                    disabled={isProcessing}
                    className={`w-full px-4 py-3 rounded font-semibold ${
                      isProcessing
                        ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-700 text-white"
                    }`}
                  >
                    {isProcessing ? "Processing..." : "Approve"}
                  </button>

                  <button
                    onClick={() => rejectApplication(app.id, app)}
                    disabled={isProcessing}
                    className={`w-full px-4 py-3 rounded font-semibold ${
                      isProcessing
                        ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                        : "bg-red-600 hover:bg-red-700 text-white"
                    }`}
                  >
                    {isProcessing ? "Processing..." : "Reject"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}